import { warRoomClient as orpcClient } from "@/warRoom/lib/client";
import type {
  AccountsPayable,
  AccountsReceivable,
  Partner,
  Product,
  Project,
  ProjectNode,
  StorageLocation,
} from "@/warRoom/lib/api-types";

import { cached, indexById } from "./cache";
import {
  ACCOUNT_KIND,
  BOOL_LABEL,
  DELIVERY_METHOD,
  EMPLOYEE_STATUS,
  NODE_TYPE,
  PARTNER_STATUS,
  PAYMENT_METHOD,
  PROJECT_STATUS,
  PURCHASE_ORDER_STATUS,
  SCAN_MODE,
  SHIPPING_FEE_CALCULATION,
  STOCK_DOC_STATUS,
} from "./enums";
import type { Dataset, Field, Row } from "./types";
import { defineI18nGetters } from "@/warRoom/lib/i18n";

/* ------------------------------ 共用欄位定義 ------------------------------ */

type FieldDef = Omit<Field, "label">;
type DatasetDef = Omit<Dataset, "label" | "description" | "fields"> & {
  fields: FieldDef[];
};

const f = {
  text: (key: string, extra?: Partial<FieldDef>): FieldDef => ({
    key,
    role: "dimension",
    format: "text",
    ...extra,
  }),
  enumField: (key: string, enumLabels: Record<string, string>): FieldDef => ({
    key,
    role: "dimension",
    format: "text",
    enumLabels,
  }),
  bool: (key: string): FieldDef => ({
    key,
    role: "dimension",
    format: "bool",
    enumLabels: BOOL_LABEL,
  }),
  time: (key: string): FieldDef => ({ key, role: "time", format: "date" }),
  int: (key: string): FieldDef => ({ key, role: "measure", format: "int" }),
  money: (key: string): FieldDef => ({
    key,
    role: "measure",
    format: "currency",
  }),
  decimal: (key: string): FieldDef => ({
    key,
    role: "measure",
    format: "decimal",
  }),
};

const localize = (d: DatasetDef): Dataset => {
  const prefix = `warRoom.datasets.${d.key}`;
  const fields = d.fields.map((fd) =>
    defineI18nGetters({ ...fd }, { label: `${prefix}.fields.${fd.key}` }),
  ) as Field[];
  return defineI18nGetters(
    { ...d, fields },
    { label: `${prefix}.label`, description: `${prefix}.description` },
  ) as Dataset;
};

/* -------------------------------- 共用 loader -------------------------------- */

const loadPartners = () => cached("partner", () => orpcClient.partner.list({}));
const loadProducts = () => cached("product", () => orpcClient.product.list({}));
const loadProjects = () => cached("project", () => orpcClient.project.list({}));
const loadProjectNodes = () =>
  cached("projectNode", () => orpcClient.projectNode.list({}));
const loadStorageLocations = () =>
  cached("storageLocation", () => orpcClient.storageLocation.list({}));
const loadItems = () => cached("item", () => orpcClient.item.list({}));
const loadBanks = () => cached("bank", () => orpcClient.bank.list({}));
const loadPurchaseOrders = () =>
  cached("purchaseOrder", () => orpcClient.purchaseOrder.list({}));
const loadAccountsReceivable = () =>
  cached("accountsReceivable", () => orpcClient.accountsReceivable.list({}));
const loadAccountsPayable = () =>
  cached("accountsPayable", () => orpcClient.accountsPayable.list({}));

const nameOf = (
  map: Map<string, { name?: string | null }>,
  id: string | null | undefined,
  fallback = "（無）",
) => (id ? (map.get(id)?.name ?? fallback) : fallback);

/** 含稅請款金額 */
const withTax = (amount: number, taxRate: number) =>
  Math.round(amount * (1 + (taxRate ?? 0) / 100));

/* --------------------------------- 財務欄位 --------------------------------- */

const ACCOUNT_FIELDS = (): FieldDef[] => [
  f.text("name", { highCardinality: true }),
  f.text("partnerName"),
  f.time("date"),
  f.money("requestedAmount"),
  f.money("amountWithTax"),
  f.int("taxRate"),
  f.money("paidAmount"),
  f.money("outstandingAmount"),
  f.int("detailCount"),
  f.bool("isFullySettled"),
];

const arRow = (r: AccountsReceivable, partners: Map<string, Partner>): Row => {
  const details = r.accountsReceivableDetail ?? [];
  const paid = details.reduce((s, d) => s + (d.amount ?? 0), 0);
  const total = withTax(r.requestedAmount, r.taxRate);
  return {
    id: r.id,
    name: r.name,
    partnerName: r.partner?.name ?? nameOf(partners, r.partnerId),
    date: r.billingDate,
    requestedAmount: r.requestedAmount,
    amountWithTax: total,
    taxRate: r.taxRate,
    paidAmount: paid,
    outstandingAmount: total - paid,
    detailCount: details.length,
    isFullySettled:
      details.length > 0 && details.every((d) => d.isSettled)
        ? "true"
        : "false",
  };
};

const apRow = (r: AccountsPayable, partners: Map<string, Partner>): Row => {
  const details = r.accountsPayableDetail ?? [];
  const paid = details.reduce((s, d) => s + (d.amount ?? 0), 0);
  const total = withTax(r.requestedAmount, r.taxRate);
  // 應付帳款主表沒有日期欄位（應收有 billingDate，應付沒有），
  // 只能拿明細裡最早的發票日期當代理日期。
  const invoiceDates = details
    .map((d) => d.invoiceDate)
    .filter((d): d is string => Boolean(d))
    .sort();
  return {
    id: r.id,
    name: r.name,
    partnerName: r.partner?.name ?? nameOf(partners, r.partnerId),
    date: invoiceDates[0] ?? null,
    requestedAmount: r.requestedAmount,
    amountWithTax: total,
    taxRate: r.taxRate,
    paidAmount: paid,
    outstandingAmount: total - paid,
    detailCount: details.length,
    isFullySettled:
      details.length > 0 && details.every((d) => d.isSettled)
        ? "true"
        : "false",
  };
};

/* ================================ 資料集清單 ================================ */

const DATASET_DEFS: DatasetDef[] = [
  /* ------------------------------- 基本資訊 ------------------------------- */
  {
    key: "employee",
    group: "basic",
    sourceRoute: "hrMainPage/employees",
    api: "employee.list",
    resource: "employee",
    fields: [
      f.text("name", { highCardinality: true }),
      f.enumField("status", EMPLOYEE_STATUS),
      f.text("gender"),
      f.text("bloodType"),
      f.time("birthday"),
      f.bool("motorbikeLicense"),
      f.bool("carLicense"),
      f.bool("isProjectReport"),
    ],
    fetch: async () =>
      (await orpcClient.employee.list({})).map((e) => ({
        id: e.id,
        name: e.name,
        status: e.status,
        gender: e.gender ?? "（未填）",
        bloodType: e.bloodType ?? "（未填）",
        birthday: e.birthday,
        motorbikeLicense: String(Boolean(e.motorbikeLicense)),
        carLicense: String(Boolean(e.carLicense)),
        isProjectReport: String(Boolean(e.isProjectReport)),
      })),
  },

  /* -------------------------------- 合作方 -------------------------------- */
  {
    key: "partner",
    group: "partner",
    sourceRoute: "hrMainPage/coop",
    api: "partner.list",
    resource: "partner",
    fields: [
      f.text("name", { highCardinality: true }),
      f.enumField("status", PARTNER_STATUS),
      f.text("invoiceType"),
      f.text("business"),
      f.time("applicationDate"),
    ],
    fetch: async () =>
      (await loadPartners()).map((p) => ({
        id: p.id,
        name: p.name,
        status: p.status,
        invoiceType: p.invoiceType ?? "（未填）",
        business: p.business ?? "（未填）",
        applicationDate: p.applicationDate,
      })),
  },

  /* ------------------------------- 專案管理 ------------------------------- */
  {
    key: "project",
    group: "project",
    sourceRoute: "hrMainPage/projectQuotation",
    api: "project.list",
    resource: "project",
    fields: [
      f.text("name"),
      f.enumField("status", PROJECT_STATUS),
      f.text("partnerName"),
      f.time("date"),
      f.int("nodeCount"),
    ],
    fetch: async () =>
      (await loadProjects()).map((p) => ({
        id: p.id,
        name: p.name,
        status: p.status,
        partnerName: p.partner?.name ?? "（無）",
        date: p.date,
        nodeCount: p.projectNode?.length ?? 0,
      })),
  },
  {
    key: "projectNode",
    group: "project",
    api: "projectNode.list",
    resource: "project",
    fields: [
      f.text("name", { highCardinality: true }),
      f.enumField("nodeType", NODE_TYPE),
      f.text("projectName"),
      f.int("pending"),
      f.int("inProgress"),
      f.int("completed"),
      f.int("totalAssemblies"),
      f.decimal("completedRatio"),
    ],
    fetch: async () =>
      (await loadProjectNodes()).map((n: ProjectNode) => {
        const s = n.steelAssemblyStats;
        const total =
          (s?.pending ?? 0) + (s?.inProgress ?? 0) + (s?.completed ?? 0);
        return {
          id: n.id,
          name: n.name ?? "（未命名）",
          nodeType:
            n.nodeType === "steelAssemblyPackage" ? "package" : n.nodeType,
          projectName: n.project?.name ?? "（無）",
          pending: s?.pending ?? 0,
          inProgress: s?.inProgress ?? 0,
          completed: s?.completed ?? 0,
          totalAssemblies: total,
          completedRatio: total
            ? Math.round(((s?.completed ?? 0) / total) * 1000) / 10
            : 0,
        };
      }),
  },
  {
    key: "workItem",
    group: "project",
    api: "workItem.list ＋ projectNode.list（前端 join 專案名稱）",
    resource: "project",
    fields: [
      f.text("name", { highCardinality: true }),
      f.text("projectName"),
      f.text("nodeName"),
      f.bool("isCompleted"),
      f.enumField("scanMode", SCAN_MODE),
      f.time("plannedCompletionDate"),
      f.int("completedCount"),
    ],
    fetch: async () => {
      const [items, nodes, projects] = await Promise.all([
        orpcClient.workItem.list({}),
        loadProjectNodes(),
        loadProjects(),
      ]);
      const nodeMap = indexById(nodes);
      const projectMap = indexById(projects as Project[]);
      return items.map((w) => {
        const node = nodeMap.get(w.projectNodeId);
        return {
          id: w.id,
          name: w.name,
          projectName:
            node?.project?.name ??
            (node ? nameOf(projectMap, node.projectId) : "（無）"),
          nodeName: node?.name ?? "（無）",
          isCompleted: String(w.isCompleted),
          scanMode: w.scanMode,
          plannedCompletionDate: w.plannedCompletionDate,
          completedCount: w.isCompleted ? 1 : 0,
        };
      });
    },
  },

  /* --------------------------------- 採購 --------------------------------- */
  {
    key: "purchaseOrder",
    group: "purchase",
    sourceRoute: "hrMainPage/purchaseOrderDraft",
    api: "purchaseOrder.list",
    resource: "purchase",
    fields: [
      f.text("name", { highCardinality: true }),
      f.enumField("status", PURCHASE_ORDER_STATUS),
      f.text("partnerName"),
      f.time("purchaseDate"),
      f.time("deliveryDate"),
      f.time("applicationDate"),
      f.enumField("deliveryMethod", DELIVERY_METHOD),
      f.enumField("shippingFeeCalculation", SHIPPING_FEE_CALCULATION),
      f.money("orderAmount"),
      f.money("actualShippingFee"),
      f.int("lineCount"),
      f.int("totalQuantity"),
      f.bool("requiresInvoice"),
    ],
    fetch: async () => {
      const [orders, partners] = await Promise.all([
        loadPurchaseOrders(),
        loadPartners(),
      ]);
      const partnerMap = indexById(partners);
      return orders.map((o) => {
        const lines = o.purchaseOrderProduct ?? [];
        return {
          id: o.id,
          name: o.name,
          status: o.status,
          partnerName: nameOf(partnerMap, o.partnerId),
          purchaseDate: o.purchaseDate,
          deliveryDate: o.deliveryDate,
          applicationDate: o.applicationDate,
          deliveryMethod: o.deliveryMethod,
          shippingFeeCalculation: o.shippingFeeCalculation ?? "（未填）",
          orderAmount: lines.reduce(
            (s, l) => s + (l.amount ?? 0) * (l.unitPrice ?? 0),
            0,
          ),
          actualShippingFee: o.actualShippingFee ?? 0,
          lineCount: lines.length,
          totalQuantity: lines.reduce((s, l) => s + (l.amount ?? 0), 0),
          requiresInvoice: String(Boolean(o.requiresInvoice)),
        };
      });
    },
  },
  {
    key: "purchaseOrderProduct",
    group: "purchase",
    api: "purchaseOrderProduct.list（前端 join 採購單／商品）",
    resource: "purchase",
    fields: [
      f.text("productName"),
      f.text("orderName"),
      f.enumField("orderStatus", PURCHASE_ORDER_STATUS),
      f.text("partnerName"),
      f.int("amount"),
      f.decimal("unitPrice"),
      f.money("lineTotal"),
    ],
    fetch: async () => {
      const [lines, orders, products, partners] = await Promise.all([
        orpcClient.purchaseOrderProduct.list({}),
        loadPurchaseOrders(),
        loadProducts(),
        loadPartners(),
      ]);
      const orderMap = indexById(orders);
      const productMap = indexById(products as Product[]);
      const partnerMap = indexById(partners);
      return lines.map((l) => {
        const order = orderMap.get(l.purchaseOrderId);
        return {
          id: l.id,
          productName: nameOf(productMap, l.productId),
          orderName: order?.name ?? "（無）",
          orderStatus: order?.status ?? "（無）",
          partnerName: order ? nameOf(partnerMap, order.partnerId) : "（無）",
          amount: l.amount ?? 0,
          unitPrice: l.unitPrice ?? 0,
          lineTotal: (l.amount ?? 0) * (l.unitPrice ?? 0),
        };
      });
    },
  },

  /* --------------------------------- 商品 --------------------------------- */
  {
    key: "product",
    group: "product",
    sourceRoute: "hrMainPage/product",
    api: "product.list（前端 join 合作方／品項）",
    resource: "inventory",
    fields: [
      f.text("name", { highCardinality: true }),
      f.text("partnerName"),
      f.text("itemName"),
      f.text("mainCategory"),
      f.text("subCategory"),
      f.text("material"),
      f.text("unit"),
      f.decimal("length"),
      f.decimal("unitWeight"),
      f.int("inventoryLowerLimit"),
    ],
    fetch: async () => {
      const [products, partners, items] = await Promise.all([
        loadProducts(),
        loadPartners(),
        loadItems(),
      ]);
      const partnerMap = indexById(partners);
      const itemMap = indexById(items);
      return products.map((p) => {
        const item = p.itemId ? itemMap.get(p.itemId) : undefined;
        return {
          id: p.id,
          name: p.name,
          partnerName: nameOf(partnerMap, p.partnerId),
          itemName: item?.name ?? "（無）",
          mainCategory: item?.mainCategory ?? "（無）",
          subCategory: item?.subCategory ?? "（無）",
          material: p.material ?? "（未填）",
          unit: p.unit ?? "（未填）",
          length: p.length ?? 0,
          unitWeight: p.unitWeight ?? 0,
          inventoryLowerLimit: p.inventoryLowerLimit ?? 0,
        };
      });
    },
  },
  {
    key: "item",
    group: "product",
    sourceRoute: "hrMainPage/itemEditor",
    api: "item.list",
    resource: "inventory",
    fields: [
      f.text("name", { highCardinality: true }),
      f.text("mainCategory"),
      f.text("subCategory"),
    ],
    fetch: async () =>
      (await loadItems()).map((i) => ({
        id: i.id,
        name: i.name,
        mainCategory: i.mainCategory ?? "（未分類）",
        subCategory: i.subCategory ?? "（未分類）",
      })),
  },

  /* --------------------------------- 倉儲 --------------------------------- */
  {
    key: "productInventory",
    group: "warehouse",
    sourceRoute: "hrMainPage/warehouse",
    api: "productInventory.list（前端 join 商品／儲位）",
    resource: "inventory",
    fields: [
      f.text("productName"),
      f.text("storageLocationName"),
      f.text("material"),
      f.int("amount"),
      f.int("inventoryUpperLimit"),
      f.decimal("utilization"),
    ],
    fetch: async () => {
      const [inv, products, locations] = await Promise.all([
        orpcClient.productInventory.list({}),
        loadProducts(),
        loadStorageLocations(),
      ]);
      const productMap = indexById(products as Product[]);
      const locationMap = indexById(locations as StorageLocation[]);
      return inv.map((r) => ({
        id: r.id,
        productName: nameOf(productMap, r.productId),
        storageLocationName: nameOf(locationMap, r.storageLocationId),
        material: productMap.get(r.productId)?.material ?? "（未填）",
        amount: r.amount ?? 0,
        inventoryUpperLimit: r.inventoryUpperLimit ?? 0,
        utilization: r.inventoryUpperLimit
          ? Math.round((r.amount / r.inventoryUpperLimit) * 1000) / 10
          : 0,
      }));
    },
  },
  {
    key: "warehouse",
    group: "warehouse",
    sourceRoute: "hrMainPage/warehouse",
    api: "warehouse.list",
    resource: "inventory",
    fields: [f.text("name"), f.bool("enable")],
    fetch: async () =>
      (await orpcClient.warehouse.list({})).map((w) => ({
        id: w.id,
        name: w.name,
        enable: String(Boolean(w.enable)),
      })),
  },
  {
    key: "storageLocation",
    group: "warehouse",
    sourceRoute: "hrMainPage/warehouse",
    api: "storageLocation.list",
    resource: "inventory",
    fields: [f.text("name"), f.bool("enable")],
    fetch: async () =>
      (await loadStorageLocations()).map((s) => ({
        id: s.id,
        name: s.name,
        enable: String(Boolean(s.enable)),
      })),
  },
  {
    key: "stockReceipt",
    group: "warehouse",
    sourceRoute: "hrMainPage/inventoryCreated",
    api: "stockReceipt.list",
    resource: "inventory",
    fields: [
      f.text("name", { highCardinality: true }),
      f.enumField("status", STOCK_DOC_STATUS),
      f.text("deliveryPerson"),
      f.time("createdAt"),
    ],
    fetch: async () =>
      (await orpcClient.stockReceipt.list({})).map((r) => ({
        id: r.id,
        name: r.name,
        status: r.status,
        deliveryPerson: r.deliveryPerson ?? "（未填）",
        createdAt: r.createdAt,
      })),
  },
  {
    key: "stockRequest",
    group: "warehouse",
    sourceRoute: "hrMainPage/stockRequestCreated",
    api: "stockRequest.list",
    resource: "inventory",
    fields: [
      f.text("name", { highCardinality: true }),
      f.enumField("status", STOCK_DOC_STATUS),
      f.text("deliveryPerson"),
      f.time("createdAt"),
    ],
    fetch: async () =>
      (await orpcClient.stockRequest.list({})).map((r) => ({
        id: r.id,
        name: r.name,
        status: r.status,
        deliveryPerson: r.deliveryPerson ?? "（未填）",
        createdAt: r.createdAt,
      })),
  },

  /* --------------------------------- 財務 --------------------------------- */
  {
    key: "financeArAp",
    group: "finance",
    sourceRoute: "hrMainPage/accountReceivable ＋ accountPayable",
    api: "accountsReceivable.list ＋ accountsPayable.list",
    resource: "finance",
    fields: [f.enumField("kind", ACCOUNT_KIND), ...ACCOUNT_FIELDS()],
    fetch: async () => {
      const [ar, ap, partners] = await Promise.all([
        loadAccountsReceivable(),
        loadAccountsPayable(),
        loadPartners(),
      ]);
      const partnerMap = indexById(partners);
      return [
        ...ar.map((r) => ({ kind: "AR", ...arRow(r, partnerMap) })),
        ...ap.map((r) => ({ kind: "AP", ...apRow(r, partnerMap) })),
      ];
    },
  },
  {
    key: "accountsReceivable",
    group: "finance",
    sourceRoute: "hrMainPage/accountReceivable",
    api: "accountsReceivable.list",
    resource: "finance",
    fields: ACCOUNT_FIELDS(),
    fetch: async () => {
      const [ar, partners] = await Promise.all([
        loadAccountsReceivable(),
        loadPartners(),
      ]);
      const partnerMap = indexById(partners);
      return ar.map((r) => arRow(r, partnerMap));
    },
  },
  {
    key: "accountsPayable",
    group: "finance",
    sourceRoute: "hrMainPage/accountPayable",
    api: "accountsPayable.list",
    resource: "finance",
    fields: ACCOUNT_FIELDS(),
    fetch: async () => {
      const [ap, partners] = await Promise.all([
        loadAccountsPayable(),
        loadPartners(),
      ]);
      const partnerMap = indexById(partners);
      return ap.map((r) => apRow(r, partnerMap));
    },
  },
  {
    key: "accountsDetail",
    group: "finance",
    api: "accountsReceivableDetail.list ＋ accountsPayableDetail.list",
    resource: "finance",
    fields: [
      f.enumField("kind", ACCOUNT_KIND),
      f.text("bankName"),
      f.enumField("paymentMethod", PAYMENT_METHOD),
      f.time("paymentDate"),
      f.time("invoiceDate"),
      f.time("checkClearanceDate"),
      f.money("amount"),
      f.money("handlingFee"),
      f.money("retentionAmount"),
      f.money("deductionAmount"),
      f.money("netAmount"),
      f.bool("isSettled"),
    ],
    fetch: async () => {
      const [arDetail, apDetail, banks] = await Promise.all([
        orpcClient.accountsReceivableDetail.list({}),
        orpcClient.accountsPayableDetail.list({}),
        loadBanks(),
      ]);
      const bankMap = indexById(banks);
      const shape =
        (kind: "AR" | "AP") =>
        (d: {
          id: string;
          bankId: string;
          paymentDate: string;
          invoiceDate: string;
          checkClearanceDate: string | null;
          amount: number;
          handlingFee: number;
          retentionAmount: number;
          deductionAmount: number;
          paymentMethod: string;
          isSettled: boolean;
        }): Row => ({
          id: d.id,
          kind,
          bankName: nameOf(bankMap, d.bankId),
          paymentMethod: d.paymentMethod,
          paymentDate: d.paymentDate,
          invoiceDate: d.invoiceDate,
          checkClearanceDate: d.checkClearanceDate,
          amount: d.amount ?? 0,
          handlingFee: d.handlingFee ?? 0,
          retentionAmount: d.retentionAmount ?? 0,
          deductionAmount: d.deductionAmount ?? 0,
          netAmount:
            (d.amount ?? 0) -
            (d.handlingFee ?? 0) -
            (d.retentionAmount ?? 0) -
            (d.deductionAmount ?? 0),
          isSettled: String(Boolean(d.isSettled)),
        });
      return [...arDetail.map(shape("AR")), ...apDetail.map(shape("AP"))];
    },
  },
  {
    key: "bank",
    group: "finance",
    sourceRoute: "hrMainPage/bank",
    api: "bank.list",
    resource: "finance",
    fields: [f.text("name"), f.money("balance")],
    fetch: async () =>
      (await orpcClient.bank.list({})).map((b) => ({
        id: b.id,
        name: b.name,
        balance: b.balance ?? 0,
      })),
  },
];

/** 全部資料集，選單吃的就是這一份 —— 沒有隱藏清單。 */
export const ALL_DATASETS: Dataset[] = DATASET_DEFS.map(localize);

/** 選單與測試吃同一份，兩個名字留著只是為了呼叫端不用改。 */
export const DATASETS: Dataset[] = ALL_DATASETS;

export const DATASET_GROUPS = [
  "basic",
  "partner",
  "project",
  "purchase",
  "product",
  "warehouse",
  "finance",
] as const;

export const datasetByKey = (key: string) =>
  ALL_DATASETS.find((d) => d.key === key);

export const fieldByKey = (dataset: Dataset, key: string) =>
  dataset.fields.find((f2) => f2.key === key);
